import { ContentType } from '@lib/content';
import { ContentRecord, MatchReason, DuplicateGroup, DuplicateDecision } from './types';

/* ============================================================
   NORMALIZE TITLE
   ============================================================ */

const titleNormalizeCache = new Map<string, string>();

const normalizeTitle = (value?: string): string => {
  if (!value) {
    return '';
  }

  const cached = titleNormalizeCache.get(value);

  if (cached !== undefined) {
    return cached;
  }

  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  titleNormalizeCache.set(value, normalized);

  return normalized;
};

/*
 * Leftover crumbs after punctuation strip, e.g. "A", "A++", "10", "4".
 * Non-Latin titles like "영웅" stay valid even when short.
 */
const isTooThinTitle = (normalized: string): boolean => {
  if (!normalized) {
    return true;
  }

  const tokens = normalized.split(' ').filter(Boolean);
  const compact = tokens.join('');
  const hasNonLatinLetter = /[^\u0000-\u007F]/.test(normalized);

  if (!compact || tokens.every(token => /^\d+$/.test(token))) {
    return true;
  }

  if (!hasNonLatinLetter && (compact.length < 3 || (tokens.length === 1 && tokens[0].length < 3))) {
    return true;
  }

  return false;
};

/* ============================================================
   TITLE SIMILARITY
   ============================================================ */

const isSimilarTitle = (first?: string, second?: string): boolean => {
  const a = normalizeTitle(first);

  const b = normalizeTitle(second);

  if (!a || !b || isTooThinTitle(a) || isTooThinTitle(b)) {
    return false;
  }

  /*
   * Exact normalized match.
   */

  if (a === b) {
    return true;
  }

  /*
   * Token comparison.
   *
   * Example:
   *
   * "The Last Kingdom"
   * "Last Kingdom The"
   */

  const aTokens = new Set(a.split(' '));

  const bTokens = new Set(b.split(' '));

  const intersection = [...aTokens].filter(token => bTokens.has(token));

  const union = new Set([...aTokens, ...bTokens]);

  const similarity = intersection.length / union.size;

  /*
   * Keep this conservative.
   *
   * 0.8 means most title words overlap.
   */

  return similarity >= 0.8;
};

/* ============================================================
   GET TITLE FIELDS
   ============================================================ */

const getTitleFields = (record: ContentRecord) => {
  return [
    {
      value: record.title,
      reason: 'TITLE_MATCH' as MatchReason,
    },

    {
      value: record.original_title,
      reason: 'ORIGINAL_TITLE_MATCH' as MatchReason,
    },

    {
      value: record.content_aka,
      reason: 'AKA_MATCH' as MatchReason,
    },

    {
      value: record.working_title,
      reason: 'WORKING_TITLE_MATCH' as MatchReason,
    },
  ].filter(item => Boolean(item.value?.trim()));
};

/* ============================================================
   MATCH REASONS
   ============================================================ */
/* ============================================================
   VIDEO CONTENT TYPES
   ============================================================ */

const DUPLICATE_VIDEO_CONTENT_TYPES = new Set<ContentType>([
  ContentType.MOVIE,
  ContentType.SERIES,
  ContentType.MICROSERIES,
]);

/* ============================================================
   CHECK VIDEO CONTENT
   ============================================================ */

const isVideoContentType = (
  contentType?: ContentType
): boolean => {
  if (
    contentType === undefined ||
    contentType === null
  ) {
    return false;
  }

  return DUPLICATE_VIDEO_CONTENT_TYPES.has(
    contentType
  );
};

/* ============================================================
   CHECK CONTENT TYPE COMPATIBILITY
   ============================================================ */

/*
 * All video content types can be compared:
 *
 * Movie       + Movie        => true
 * Movie       + TV Series    => true
 * Movie       + Microseries  => true
 * TV Series   + Microseries  => true
 *
 * Non-video content is excluded:
 *
 * Movie       + Book         => false
 * Movie       + Podcast      => false
 * Series      + Book         => false
 */

const isCompatibleContentType = (
  first: ContentRecord,
  second: ContentRecord
): boolean => {
  return (
    isVideoContentType(
      first.content_type
    ) &&
    isVideoContentType(
      second.content_type
    )
  );
};

export const getMatchReasons = (
  first: ContentRecord,
  second: ContentRecord
): MatchReason[] => {
  /*
   * IMPORTANT:
   *
   * Only compare video content.
   *
   * Movie / TV Series / Microseries
   * can all be compared with each other.
   *
   * Book / Podcast / Game / etc.
   * are ignored.
   */

  if (
    !isCompatibleContentType(
      first,
      second
    )
  ) {
    return [];
  }

  const reasons =
    new Set<MatchReason>();

  /* ==========================================================
     RULE 1
     TITLE / ORIGINAL / AKA / WORKING TITLE
     ========================================================== */

  const firstTitles =
    getTitleFields(first);

  const secondTitles =
    getTitleFields(second);

  for (
    const firstTitle of firstTitles
  ) {
    for (
      const secondTitle of secondTitles
    ) {
      if (
        isSimilarTitle(
          firstTitle.value,
          secondTitle.value
        )
      ) {
        reasons.add(firstTitle.reason);
        reasons.add(secondTitle.reason);
      }
    }
  }

  /* ==========================================================
     RULE 2
     IMDb ID
     ========================================================== */

  const firstImdb =
    first.imdb_id
      ?.trim()
      .toLowerCase();

  const secondImdb =
    second.imdb_id
      ?.trim()
      .toLowerCase();

  if (
    firstImdb &&
    secondImdb &&
    firstImdb === secondImdb
  ) {
    reasons.add(
      'IMDB_MATCH'
    );
  }

  /* ==========================================================
     RULE 3
     TITLE + SAME CONTENT TYPE
     ========================================================== */

  const anyTitleMatch = reasons.has('TITLE_MATCH')
    || reasons.has('ORIGINAL_TITLE_MATCH')
    || reasons.has('AKA_MATCH')
    || reasons.has('WORKING_TITLE_MATCH');

  if (
    anyTitleMatch &&
    first.content_type !== undefined &&
    first.content_type !== null &&
    first.content_type === second.content_type
  ) {
    reasons.add('TITLE_CONTENT_TYPE_MATCH');
  }

  return Array.from(
    reasons
  );
};


const toRecordId = (value?: string | number | null): string => {
  return String(value ?? '').trim();
};

const createGroupKey = (
  vtrIds: Array<string | number>
): string => {
  return [...vtrIds]
    .map(toRecordId)
    .filter(Boolean)
    .sort()
    .join('|');
};

/* ============================================================
   FIND DUPLICATE GROUPS
   ============================================================ */

const TITLE_FIELD_PREFIXES = [
  ['t', 'title'],
  ['o', 'original_title'],
  ['a', 'content_aka'],
  ['w', 'working_title'],
] as const;

const TITLE_STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'to']);
const MAX_TOKEN_BUCKET = 250;
const ANALYSIS_YIELD_EVERY = 250;

const yieldToUi = () =>
  new Promise<void>(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });

const addToBucket = (
  buckets: Map<string, string[]>,
  key: string,
  id: string
) => {
  const existing = buckets.get(key);

  if (existing) {
    existing.push(id);
    return;
  }

  buckets.set(key, [id]);
};

const buildCandidateIndex = (records: ContentRecord[]) => {
  const byId = new Map<string, ContentRecord>();
  const buckets = new Map<string, string[]>();

  records.forEach(record => {
    const id = toRecordId(record.vtr_id);

    if (!id || byId.has(id)) {
      return;
    }

    byId.set(id, record);

    const imdb = record.imdb_id?.trim().toLowerCase();

    if (imdb) {
      addToBucket(buckets, `imdb:${imdb}`, id);
    }

    TITLE_FIELD_PREFIXES.forEach(([prefix, field]) => {
      const value = record[field];
      const normalized = normalizeTitle(value);

      if (!normalized || isTooThinTitle(normalized)) {
        return;
      }

      addToBucket(buckets, `${prefix}:${normalized}`, id);
      addToBucket(buckets, `any:${normalized}`, id);

      normalized.split(' ').forEach(token => {
        if (token.length < 3 || TITLE_STOP_WORDS.has(token)) {
          return;
        }

        addToBucket(buckets, `tok:${prefix}:${token}`, id);
        addToBucket(buckets, `tok:any:${token}`, id);
      });
    });
  });

  return { byId, buckets };
};

const getCandidateRecords = (
  record: ContentRecord,
  index: ReturnType<typeof buildCandidateIndex>
): ContentRecord[] => {
  const recordId = toRecordId(record.vtr_id);
  const candidateIds = new Set<string>();

  const addBucket = (key: string, ignoreLargeTokenBucket = false) => {
    const bucket = index.buckets.get(key);

    if (!bucket) {
      return;
    }

    if (ignoreLargeTokenBucket && bucket.length > MAX_TOKEN_BUCKET) {
      return;
    }

    bucket.forEach(id => {
      if (id !== recordId) {
        candidateIds.add(id);
      }
    });
  };

  const imdb = record.imdb_id?.trim().toLowerCase();

  if (imdb) {
    addBucket(`imdb:${imdb}`);
  }

  TITLE_FIELD_PREFIXES.forEach(([prefix, field]) => {
    const normalized = normalizeTitle(record[field]);

    if (!normalized || isTooThinTitle(normalized)) {
      return;
    }

    addBucket(`${prefix}:${normalized}`);
    addBucket(`any:${normalized}`);

    normalized.split(' ').forEach(token => {
      if (token.length < 3 || TITLE_STOP_WORDS.has(token)) {
        return;
      }

      addBucket(`tok:${prefix}:${token}`, true);
      addBucket(`tok:any:${token}`, true);
    });
  });

  const candidates: ContentRecord[] = [];

  candidateIds.forEach(id => {
    const candidate = index.byId.get(id);

    if (candidate) {
      candidates.push(candidate);
    }
  });

  return candidates;
};

const expandDuplicateGroup = (
  record: ContentRecord,
  index: ReturnType<typeof buildCandidateIndex>,
  visited: Set<string>
): { records: ContentRecord[]; matchReasons: MatchReason[] } | null => {
  const recordId = toRecordId(record.vtr_id);

  if (!recordId || visited.has(recordId)) {
    return null;
  }

  const queue: ContentRecord[] = [record];
  visited.add(recordId);

  const groupRecords: ContentRecord[] = [];
  const groupReasons = new Set<MatchReason>();

  while (queue.length > 0) {
    const current = queue.shift()!;

    groupRecords.push(current);

    const candidates = getCandidateRecords(current, index);

    for (const candidate of candidates) {
      const candidateId = toRecordId(candidate.vtr_id);

      if (!candidateId || visited.has(candidateId)) {
        continue;
      }

      const reasons = getMatchReasons(current, candidate);

      if (reasons.length > 0) {
        reasons.forEach(reason => groupReasons.add(reason));
        visited.add(candidateId);
        queue.push(candidate);
      }
    }
  }

  if (groupRecords.length < 2) {
    return null;
  }

  return {
    records: groupRecords,
    matchReasons: Array.from(groupReasons),
  };
};

const toDuplicateGroup = (
  group: { records: ContentRecord[]; matchReasons: MatchReason[] },
  index: number
): DuplicateGroup => ({
  id: `DG-${String(index + 1).padStart(3, '0')}`,
  groupKey: createGroupKey(group.records.map(item => item.vtr_id)),
  records: group.records,
  matchReasons: group.matchReasons,
});

export const findDuplicateGroups = (
  records: ContentRecord[]
): DuplicateGroup[] => {
  if (records.length < 2) {
    return [];
  }

  const index = buildCandidateIndex(records);
  const uniqueRecords = Array.from(index.byId.values());
  const visited = new Set<string>();
  const groups: DuplicateGroup[] = [];

  uniqueRecords.forEach(record => {
    const group = expandDuplicateGroup(record, index, visited);

    if (group) {
      groups.push(toDuplicateGroup(group, groups.length));
    }
  });

  return groups;
};

export const findDuplicateGroupsAsync = async (
  records: ContentRecord[],
  onProgress?: (processed: number, total: number) => void
): Promise<DuplicateGroup[]> => {
  if (records.length < 2) {
    return [];
  }

  const index = buildCandidateIndex(records);
  const uniqueRecords = Array.from(index.byId.values());
  const visited = new Set<string>();
  const groups: DuplicateGroup[] = [];

  for (let recordIndex = 0; recordIndex < uniqueRecords.length; recordIndex += 1) {
    const group = expandDuplicateGroup(
      uniqueRecords[recordIndex],
      index,
      visited
    );

    if (group) {
      groups.push(toDuplicateGroup(group, groups.length));
    }

    if (recordIndex > 0 && recordIndex % ANALYSIS_YIELD_EVERY === 0) {
      onProgress?.(recordIndex, uniqueRecords.length);
      await yieldToUi();
    }
  }

  onProgress?.(uniqueRecords.length, uniqueRecords.length);

  return groups;
};

/* ============================================================
   REGROUP OVERLAPPING DUPLICATE GROUPS
   ============================================================ */

const recordRichness = (record: ContentRecord): number => {
  return [
    record.title,
    record.original_title,
    record.content_aka,
    record.working_title,
    record.imdb_id,
    record.genres,
    record.poster_path,
    record.created_at,
    record.modified_at,
    record.release_date,
    record.lang_names,
    record.origin_country,
    record.status,
  ].filter(Boolean).length;
};

const pickRicherRecord = (
  first: ContentRecord,
  second: ContentRecord
): ContentRecord => {
  return recordRichness(first) >= recordRichness(second)
    ? first
    : second;
};

const uniqueRecordsById = (
  records: ContentRecord[]
): ContentRecord[] => {
  const recordsById = new Map<string, ContentRecord>();

  records.forEach(record => {
    const recordId = toRecordId(record.vtr_id);

    if (!recordId) {
      return;
    }

    const normalizedRecord = {
      ...record,
      vtr_id: recordId,
    };

    const existing = recordsById.get(recordId);

    if (!existing) {
      recordsById.set(recordId, normalizedRecord);
      return;
    }

    recordsById.set(
      recordId,
      pickRicherRecord(existing, normalizedRecord)
    );
  });

  return Array.from(recordsById.values());
};

const getNextDuplicateGroupId = (
  groups: DuplicateGroup[],
  reservedIds: Set<string>
): string => {
  let max = 0;

  groups.forEach(group => {
    const match = /^DG-(\d+)$/.exec(group.id);

    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  });

  let next = max + 1;
  let id = `DG-${String(next).padStart(3, '0')}`;

  while (reservedIds.has(id)) {
    next += 1;
    id = `DG-${String(next).padStart(3, '0')}`;
  }

  return id;
};

const preserveDuplicateGroupIds = (
  previousGroups: DuplicateGroup[],
  regrouped: DuplicateGroup[]
): DuplicateGroup[] => {
  const usedIds = new Set<string>();

  return regrouped.map(group => {
    const incomingIds = new Set(
      group.records.map(record => toRecordId(record.vtr_id))
    );

    let bestMatch: DuplicateGroup | undefined;
    let bestOverlap = 0;

    previousGroups.forEach(previous => {
      if (usedIds.has(previous.id) || !previous.id.startsWith('DG-')) {
        return;
      }

      const overlap = previous.records.filter(record =>
        incomingIds.has(toRecordId(record.vtr_id))
      ).length;

      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestMatch = previous;
      }
    });

    if (bestMatch && bestOverlap > 0) {
      usedIds.add(bestMatch.id);

      return {
        ...group,
        id: bestMatch.id,
      };
    }

    const nextId = getNextDuplicateGroupId(previousGroups, usedIds);
    usedIds.add(nextId);

    return {
      ...group,
      id: nextId,
    };
  });
};

const resolveNonDuplicateRecordIds = (
  decision: DuplicateDecision
): string[] => {
  if (decision.selected_vtr_ids?.length) {
    return decision.selected_vtr_ids.map(toRecordId).filter(Boolean);
  }

  if (decision.vtr_id_1 && decision.vtr_id_2) {
    return [
      toRecordId(decision.vtr_id_1),
      toRecordId(decision.vtr_id_2),
    ].filter(Boolean);
  }

  if (decision.pair_key) {
    return decision.pair_key.split('|').map(toRecordId).filter(Boolean);
  }

  const contentIds = Object.keys(
    decision.content_data ||
    {}
  );

  if (contentIds.length > 0) {
    return contentIds.map(toRecordId).filter(Boolean);
  }

  return (decision.group_vtr_ids || []).map(toRecordId).filter(Boolean);
};

export const getNonDuplicateRecordIds = (
  decisions: DuplicateDecision[]
): Set<string> => {
  const ids = new Set<string>();

  if (!Array.isArray(decisions)) {
    return ids;
  }

  decisions.forEach(decision => {
    resolveNonDuplicateRecordIds(decision).forEach(id => ids.add(id));
  });

  return ids;
};

export const hydrateGroupRecords = (
  groups: DuplicateGroup[],
  catalog: ContentRecord[]
): DuplicateGroup[] => {
  const catalogById = new Map(
    catalog.map(record => [toRecordId(record.vtr_id), record] as const)
  );

  return groups.map(group => ({
    ...group,
    records: group.records.map(record => {
      const catalogRecord = catalogById.get(toRecordId(record.vtr_id));

      return catalogRecord
        ? pickRicherRecord(catalogRecord, record)
        : record;
    }),
  }));
};

export const getGroupRecordIds = (
  groups: DuplicateGroup[]
): string[] => {
  return groups.flatMap(group =>
    group.records.map(record => toRecordId(record.vtr_id)).filter(Boolean)
  );
};

export const findGroupContainingRecords = (
  groups: DuplicateGroup[],
  recordIds: Array<string | number>
): DuplicateGroup | undefined => {
  const incomingIds = new Set(recordIds.map(toRecordId).filter(Boolean));

  return groups.find(group =>
    group.records.some(record => incomingIds.has(toRecordId(record.vtr_id)))
  );
};

const parseTimestamp = (value?: string): number => {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

type NonDuplicateHold = {
  selectedIds: Set<string>;
  frozenIds: Set<string>;
  decidedAt: number;
};

const toNonDuplicateHolds = (
  groups: DuplicateGroup[] = []
): NonDuplicateHold[] => {
  return groups.map(group => {
    const selectedIds = new Set(
      group.records.map(record => toRecordId(record.vtr_id)).filter(Boolean)
    );
    const frozenIds = new Set(
      (group.sourceGroupVtrIds?.length
        ? group.sourceGroupVtrIds
        : group.records.map(record => record.vtr_id)
      )
        .map(toRecordId)
        .filter(Boolean)
    );

    return {
      selectedIds,
      frozenIds,
      decidedAt: parseTimestamp(group.createdAt),
    };
  });
};

const hasNewContentAfterHold = (
  group: DuplicateGroup,
  holds: NonDuplicateHold[]
): boolean => {
  return group.records.some(record => {
    const recordId = toRecordId(record.vtr_id);
    const createdAt = parseTimestamp(record.created_at);

    return holds.some(hold => {
      if (hold.selectedIds.has(recordId)) {
        return false;
      }

      if (hold.decidedAt && createdAt > hold.decidedAt) {
        return true;
      }

      const frozenHasLeftovers = [...hold.frozenIds].some(
        id => !hold.selectedIds.has(id)
      );

      return frozenHasLeftovers && !hold.frozenIds.has(recordId);
    });
  });
};

/*
 * Keep ND holds in place (ABC + AB marked ND → do not show ABC again).
 * If a newer matching title appears after the ND decision, reopen the
 * full cluster including the held records (ABCD).
 */
const applyNonDuplicateHolds = (
  groups: DuplicateGroup[],
  nonDuplicateGroups: DuplicateGroup[] = []
): DuplicateGroup[] => {
  if (!nonDuplicateGroups.length) {
    return groups;
  }

  const holds = toNonDuplicateHolds(nonDuplicateGroups);
  const reopened: DuplicateGroup[] = [];
  const rest: DuplicateGroup[] = [];

  groups.forEach(group => {
    const overlapping = holds.filter(hold =>
      group.records.some(record =>
        hold.selectedIds.has(toRecordId(record.vtr_id))
      )
    );

    if (overlapping.length === 0) {
      rest.push(group);
      return;
    }

    if (hasNewContentAfterHold(group, overlapping)) {
      reopened.push(group);
    }
  });

  return [...reopened, ...rest];
};

export const releaseSupersededNonDuplicateGroups = (
  nonDuplicateGroups: DuplicateGroup[],
  duplicateGroups: DuplicateGroup[]
): DuplicateGroup[] => {
  if (!nonDuplicateGroups.length || !duplicateGroups.length) {
    return nonDuplicateGroups;
  }

  const duplicateIds = new Set(
    duplicateGroups.flatMap(group =>
      group.records.map(record => toRecordId(record.vtr_id)).filter(Boolean)
    )
  );

  return nonDuplicateGroups.filter(group =>
    !group.records.every(record =>
      duplicateIds.has(toRecordId(record.vtr_id))
    )
  );
};

/*
 * Rebuild duplicate groups from the content catalog after
 * NDG membership changes.
 *
 * Prefer this over appending an NDG as a second card.
 *
 * Example:
 *   DG-004 = A, B, C, D
 *   NDG    = B, D
 *
 * After B,D are held: leftover matches stay (A, C if they still match).
 * ABC + AB marked ND stays hidden until a newer matching title arrives.
 * After B,D are marked duplicate again: one group A, B, C, D.
 */
type RebuildDuplicateGroupOptions = {
  excludeIds?: Iterable<string | number>;
  extraRecords?: ContentRecord[];
  forceIncludeRecords?: ContentRecord[];
  previousGroups?: DuplicateGroup[];
  nonDuplicateGroups?: DuplicateGroup[];
};

const collectRebuildRecords = (
  catalog: ContentRecord[],
  options: RebuildDuplicateGroupOptions
): ContentRecord[] => {
  const excludedIds = new Set(
    [...(options.excludeIds || [])].map(toRecordId).filter(Boolean)
  );

  return uniqueRecordsById([
    ...catalog,
    ...(options.extraRecords || []),
    ...(options.forceIncludeRecords || []),
    ...(options.nonDuplicateGroups || []).flatMap(group => group.records),
  ]).filter(record => !excludedIds.has(toRecordId(record.vtr_id)));
};

const finalizeRebuiltGroups = (
  nextGroups: DuplicateGroup[],
  options: RebuildDuplicateGroupOptions
): DuplicateGroup[] => {
  const held = applyNonDuplicateHolds(
    nextGroups,
    options.nonDuplicateGroups
  );
  const preserved = options.previousGroups?.length
    ? preserveDuplicateGroupIds(options.previousGroups, held)
    : held;

  if (!options.forceIncludeRecords?.length) {
    return preserved;
  }

  return mergeIncomingDuplicateRecords(
    preserved,
    options.forceIncludeRecords
  );
};

export const rebuildDuplicateGroups = (
  catalog: ContentRecord[],
  options: RebuildDuplicateGroupOptions = {}
): DuplicateGroup[] => {
  const records = collectRebuildRecords(catalog, options);

  if (records.length < 2 && !options.forceIncludeRecords?.length) {
    return [];
  }

  const nextGroups = records.length >= 2 ? findDuplicateGroups(records) : [];

  return finalizeRebuiltGroups(nextGroups, options);
};

export const rebuildDuplicateGroupsAsync = async (
  catalog: ContentRecord[],
  options: RebuildDuplicateGroupOptions = {},
  onProgress?: (processed: number, total: number) => void
): Promise<DuplicateGroup[]> => {
  const records = collectRebuildRecords(catalog, options);

  if (records.length < 2 && !options.forceIncludeRecords?.length) {
    return [];
  }

  const nextGroups =
    records.length >= 2
      ? await findDuplicateGroupsAsync(records, onProgress)
      : [];

  return finalizeRebuiltGroups(nextGroups, options);
};

/*
 * Merge overlapping / matching group cards back into
 * connected components. Used when an NDG is dropped
 * onto the current duplicate-group list without a
 * full catalog rescan.
 */
export const regroupDuplicateGroups = (
  groups: DuplicateGroup[],
  catalog: ContentRecord[] = []
): DuplicateGroup[] => {
  return rebuildDuplicateGroups(catalog, {
    extraRecords: groups.flatMap(group => group.records),
    previousGroups: groups,
  });
};

export const refineDuplicateGroups = (
  duplicateGroups: DuplicateGroup[],
  nonDuplicateDecisions: DuplicateDecision[]
): DuplicateGroup[] => {
  if (
    !Array.isArray(nonDuplicateDecisions) ||
    nonDuplicateDecisions.length === 0
  ) {
    return duplicateGroups;
  }

  return rebuildDuplicateGroups(
    duplicateGroups.flatMap(group => group.records),
    {
      excludeIds: getNonDuplicateRecordIds(nonDuplicateDecisions),
      previousGroups: duplicateGroups,
    }
  );
};

/* ============================================================
   NORMALIZE GROUP ID
   ============================================================ */
const normalizeMatchReasons = (
  reasons: string[]
): MatchReason[] => {
  return reasons.filter(
    (reason): reason is MatchReason =>
      [
        'IMDb Match',
        'Title Match',
        'Working Title',
        'Original Title',
        'AKA Match',
      ].includes(reason)
  );
};

const collectMatchReasons = (
  records: ContentRecord[]
): MatchReason[] => {
  const reasons = new Set<MatchReason>();

  for (let index = 0; index < records.length; index += 1) {
    for (let next = index + 1; next < records.length; next += 1) {
      getMatchReasons(records[index], records[next]).forEach(reason => {
        reasons.add(reason);
      });
    }
  }

  return Array.from(reasons);
};

export const finalizeNonDuplicateGroups = (
  groups: DuplicateGroup[]
): DuplicateGroup[] => {
  return [...groups]
    .sort((left, right) =>
      String(right.createdAt || '').localeCompare(String(left.createdAt || ''))
    )
    .map((group, index) => ({
      ...group,
      id: `NDG-${String(index + 1).padStart(3, '0')}`,
    }));
};

/*
 * Pair decisions A+B, A+C, B+C are one connected set.
 * Collapse them to a single group A+B+C.
 */
export const mergeOverlappingNonDuplicateGroups = (
  groups: DuplicateGroup[]
): DuplicateGroup[] => {
  if (groups.length <= 1) {
    return finalizeNonDuplicateGroups(groups);
  }

  const parent = new Map<string, string>();

  const find = (id: string): string => {
    const root = parent.get(id) ?? id;

    if (root !== id) {
      const resolved = find(root);
      parent.set(id, resolved);
      return resolved;
    }

    return root;
  };

  const union = (left: string, right: string) => {
    const rootLeft = find(left);
    const rootRight = find(right);

    if (rootLeft !== rootRight) {
      parent.set(rootRight, rootLeft);
    }
  };

  groups.forEach(group => {
    const ids = group.records
      .map(record => toRecordId(record.vtr_id))
      .filter(Boolean);

    ids.forEach(id => {
      if (!parent.has(id)) {
        parent.set(id, id);
      }
    });

    for (let index = 1; index < ids.length; index += 1) {
      union(ids[0], ids[index]);
    }
  });

  const clusters = new Map<string, DuplicateGroup[]>();

  groups.forEach(group => {
    const firstId = toRecordId(group.records[0]?.vtr_id);

    if (!firstId) {
      return;
    }

    const root = find(firstId);
    const cluster = clusters.get(root) || [];
    cluster.push(group);
    clusters.set(root, cluster);
  });

  return finalizeNonDuplicateGroups(
    Array.from(clusters.values()).map(cluster => {
      const records = uniqueRecordsById(
        cluster.flatMap(group => group.records)
      );
      const latest = cluster.reduce((current, group) => {
        return (group.createdAt || '') > (current.createdAt || '')
          ? group
          : current;
      }, cluster[0]);

      return {
        id: latest.id,
        groupKey: createGroupKey(records.map(record => record.vtr_id)),
        records,
        matchReasons: Array.from(
          new Set(cluster.flatMap(group => group.matchReasons || []))
        ),
        createdBy: latest?.createdBy,
        createdAt: latest?.createdAt,
        sourceGroupVtrIds: Array.from(
          new Set(
            cluster.flatMap(group =>
              group.sourceGroupVtrIds?.length
                ? group.sourceGroupVtrIds
                : group.records.map(record => toRecordId(record.vtr_id))
            )
          )
        ).filter(Boolean),
      };
    })
  );
};

export const mergeIncomingDuplicateRecords = (
  groups: DuplicateGroup[],
  incoming: ContentRecord[]
): DuplicateGroup[] => {
  const incomingRecords = uniqueRecordsById(incoming);

  if (incomingRecords.length === 0) {
    return groups;
  }

  const incomingIds = new Set(
    incomingRecords.map(record => toRecordId(record.vtr_id))
  );
  const overlapping: DuplicateGroup[] = [];
  const rest: DuplicateGroup[] = [];

  groups.forEach(group => {
    const sharesId = group.records.some(record =>
      incomingIds.has(toRecordId(record.vtr_id))
    );
    const sharesMatch =
      !sharesId &&
      group.records.some(record =>
        incomingRecords.some(
          item => getMatchReasons(record, item).length > 0
        )
      );

    if (sharesId || sharesMatch) {
      overlapping.push(group);
    } else {
      rest.push(group);
    }
  });

  const records = uniqueRecordsById([
    ...overlapping.flatMap(group => group.records),
    ...incomingRecords,
  ]);

  if (records.length < 2) {
    return groups;
  }

  const host = overlapping[0];
  const usedIds = new Set(groups.map(group => group.id));

  return [
    ...rest,
    {
      id:
        host?.id ||
        getNextDuplicateGroupId(groups, usedIds),
      groupKey: createGroupKey(records.map(record => record.vtr_id)),
      records,
      matchReasons: collectMatchReasons(records),
    },
  ];
};

export const normalizeNonDuplicateGroups = (
  decisions: DuplicateDecision[]
): DuplicateGroup[] => {
  const groups = new Map<string, DuplicateGroup>();

  decisions.forEach((decision) => {
    const recordIds = resolveNonDuplicateRecordIds(decision);

    if (recordIds.length === 0) {
      return;
    }

    const groupKey = createGroupKey(recordIds);

    if (groups.has(groupKey)) {
      return;
    }

    const contentData =
      decision.latest_content_data ||
      decision.content_data ||
      {};

    const records = recordIds.map((vtrId) => {
      const content =
        contentData[vtrId] ||
        contentData[String(vtrId)];

      return {
        vtr_id: vtrId,
        title: content?.title || '',
        original_title: content?.original_title || '',
        content_aka: content?.content_aka || '',
        working_title: content?.working_title || '',
        imdb_id: content?.imdb_id || '',
        content_type: content?.content_type ?? null,
        lang_names: content?.lang_names || '',
        origin_country: content?.origin_country || '',
      };
    });

    groups.set(groupKey, {
      id: `NDG-${String(groups.size + 1).padStart(3, '0')}`,
      groupKey,
      records,
      matchReasons: normalizeMatchReasons(
        decision.match_reasons || []
      ),
      createdBy: decision.created_by || '',
      createdAt: decision.created_at || '',
      sourceGroupVtrIds: Array.from(
        new Set(
          (decision.group_vtr_ids || recordIds).map(toRecordId).filter(Boolean)
        )
      ),
    });
  });

  return mergeOverlappingNonDuplicateGroups(Array.from(groups.values()));
};